import { useEffect, useRef, useState } from 'react';

/**
 * Drag rail that resizes the sidebar by updating its CSS variable. Mirrors the
 * pattern used by ChatSidebar so resize feels snappy: width lives in React
 * state, a ref tracks the active drag (no re-renders during mousedown), and
 * iframe pointer-events are disabled while dragging so embedded dashboards
 * don't swallow mousemove events and stutter the rail.
 *
 * Persisted to localStorage so the chosen width survives page reloads.
 */
const SIDEBAR_WIDTH_KEY = 'platform.sidebar.width';
const DEFAULT_WIDTH = 256;
const MIN_WIDTH = 200;
const MAX_WIDTH = 420;

const clamp = (value: number) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, value));

// Temporarily suppress iframe input so embedded dashboards don't capture the
// resize drag's mousemove events. Re-enabled on mouseup.
/* eslint-disable no-param-reassign */
const setIframePointerEvents = (value: 'none' | '') => {
  document.querySelectorAll<HTMLIFrameElement>('iframe').forEach((iframe) => {
    iframe.style.pointerEvents = value;
  });
};
/* eslint-enable no-param-reassign */

const SidebarResizeRail = () => {
  const [width, setWidth] = useState<number>(() => {
    try {
      const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
      const parsed = saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
      return Number.isFinite(parsed) ? clamp(parsed) : DEFAULT_WIDTH;
    } catch {
      return DEFAULT_WIDTH;
    }
  });
  const isResizingRef = useRef(false);

  // Apply the current width to the wrapper's CSS variable. The wrapper is the
  // SidebarProvider root that defines --sidebar-width inline, so overriding it
  // here propagates to every consumer of the variable inside the sidebar.
  useEffect(() => {
    const wrapper = document.querySelector<HTMLElement>('.group\\/sidebar-wrapper');
    if (wrapper) wrapper.style.setProperty('--sidebar-width', `${width}px`);
  }, [width]);

  // localStorage writes are synchronous and block the main thread (~0.5–2ms
  // each). Persisting on every mousemove frame (~60 writes/sec during a drag)
  // is wasted work and adds jank. Persist only when the drag settles — see
  // stopResizing below.
  const persistWidth = (value: number) => {
    try {
      localStorage.setItem(SIDEBAR_WIDTH_KEY, String(value));
    } catch {
      // localStorage can throw under Safari Private Mode / quota — ignore.
    }
  };

  const onMouseMove = (event: MouseEvent) => {
    if (!isResizingRef.current) return;
    // Sidebar is pinned to the left edge, so the new width simply equals the
    // cursor's X position (clamped to the allowed range).
    setWidth(clamp(event.clientX));
  };

  const stopResizing = () => {
    if (!isResizingRef.current) return;
    isResizingRef.current = false;
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    // Re-enable the sidebar's width transition once the drag is done so
    // collapse/expand animations still feel smooth.
    document.body.classList.remove('sidebar-resizing');
    setIframePointerEvents('');
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', stopResizing);
    // Read the live width off the wrapper and persist once. Avoids relying on
    // a possibly-stale closure capture of the `width` state.
    const wrapper = document.querySelector<HTMLElement>('.group\\/sidebar-wrapper');
    const current = wrapper?.style.getPropertyValue('--sidebar-width');
    const parsed = current ? parseInt(current, 10) : NaN;
    persistWidth(Number.isFinite(parsed) ? clamp(parsed) : DEFAULT_WIDTH);
  };

  const startResizing = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    isResizingRef.current = true;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    // Kill the width transition (200ms ease-linear baked into the shadcn
    // sidebar) so the drag tracks the cursor 1:1 instead of catching up.
    document.body.classList.add('sidebar-resizing');
    setIframePointerEvents('none');
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', stopResizing);
  };

  return (
    // The rail is a pointer-only resize handle; it has no keyboard equivalent today.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize sidebar"
      onMouseDown={startResizing}
      className="absolute right-0 top-0 z-30 hidden h-full w-[2px] cursor-col-resize select-none bg-transparent transition-colors hover:bg-brand-500/30 group-data-[collapsible=icon]:!hidden md:block"
    />
  );
};

export default SidebarResizeRail;
